import { css } from "@linaria/core";
import cn from "classnames";
import React, { useMemo } from "react";
import { Outlet } from "react-router";
import { type Project } from "../../../../../../api/useProjects";
import { useProjectSource } from "../../../../../../api/useSources";
import { colors } from "@ui/Theme";
import Header from "../components/Header";
import { Tag } from "@ui/Tag";
import TitleHeader from "../components/Header/TitleHeader";
import Option from "@ui/Option";
import { color } from "@ui";
import t from "../../../../../../i18n";
import { DeleteBinLine } from "@ui/IconV2/react";
import { useModals } from "@ui/lib/hooks/useModal";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import Modal from "@ui/Modal";
import ModalFormRemoveManualFaultEvent from "../../../components/TransactionalModals/ModalFormRemoveManualFaultEvent";
import SuspenseSidePanel from "../components/SidePanel/SuspenseSidePanel";
import ResumeCard from "../components/Header/EventCardHeader/ResumeCard";
import { EventNavigation } from "../components/Header/EventNavigation";
import { useSidePanelSelection } from "../../../hooks/useSidePanelSelection";
import {
  useSelectedSidePanelManualFaultEvent,
  type SelectedSidePanelManualFaultEvent,
} from "../../../hooks/createGlobalStates/useSelectedSidePanelManualFaultEvent";
import Activity from "./components/SidePanel/Activity";
import LastCommentCard from "../components/Header/EventCardHeader/LastCommentCard";
import type { ManualEvent } from "../../../../../../api/useEvents";
import SidePanelEditForm from "./components/SidePanel/SidePanelEditForm";
import Details from "./components/SidePanel/Details";
import ActionIdentifiedCard from "../components/Header/EventCardHeader/ActionIdentifiedCard";
import { useEventHistories } from "../../../../../../api/events/histories";

const EXTEND_CONTENT = "extendContent";
const DELETE_MANUAL_FAULT_EVENT_MODAL = "delete-manual-fault-event-modal";

export interface ManualFaultEventOutletContext {
  project: Project;
  event: ManualEvent;
}

const ManualFaultEvent = ({ project, event }: { project: Project; event: ManualEvent }) => {
  const canUserEditProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const { modal, closeModal, openModal } = useModals<string>();
  const source = useProjectSource(project);
  if (source === null) throw new Error("Source is undefined");

  const manualFaultEventsHistory = useEventHistories({
    projectId: event.projectId,
    manualFaultId: event.manualFaultId,
    manualFaultEventId: event.id,
  });

  const manualFaultEventHistoryCount = useMemo(
    () => manualFaultEventsHistory.reduce((acc, { events }) => acc + events.length, 0),
    [manualFaultEventsHistory],
  );

  const { isFolded, setFolded, selectedSidePanel, setSelectedSidePanel, onClickSidePanel } =
    useSidePanelSelection<SelectedSidePanelManualFaultEvent>({
      scope: project.id,
      useSelectionState: useSelectedSidePanelManualFaultEvent,
    });

  React.useEffect(() => {
    setFolded(true);
  }, [event.id, setFolded]);

  return (
    <>
      <div
        className={cn(manualFaultEvent, {
          isFolded,
        })}
      >
        <div className={cn(manualFaultEventContent)}>
          <Header
            titleHeader={
              <TitleHeader
                projectId={project.id}
                name={event.name}
                hasMultiFault={event.failureNames.length > 1}
                status={event.metadata.status}
                priority={event.priority}
                reparability={event.reparability}
                optionsMenu={
                  <Option onClick={() => openModal(DELETE_MANUAL_FAULT_EVENT_MODAL)} closeOnClick>
                    <DeleteBinLine size="s" color={color.carbon800} />
                    <span>{t("project.manual_fault_event.header.menu.option.delete")}</span>
                  </Option>
                }
                onClickComment={onClickSidePanel({ type: "comments" })}
                onClickActivity={onClickSidePanel({ type: "activity" })}
                isCommentActive={!isFolded && selectedSidePanel.type === "comments"}
                isActivityActive={!isFolded && selectedSidePanel.type === "activity"}
                action={onClickSidePanel({
                  type: "edit",
                })}
                optionBadges={[
                  <Tag key="manual" className={tag} size="s">
                    {t("project.manual_fault_event.tag.manual")}
                  </Tag>,
                ]}
              />
            }
            cards={{
              resumeCard: (
                <ResumeCard
                  duration={{
                    startsAt: event.startsAt,
                    endsAt: event.metadata.endsAt,
                    timeZone: project.timezone,
                  }}
                  faultNames={[event.metadata.manualFaultName]}
                  historyCount={manualFaultEventHistoryCount}
                  onClick={onClickSidePanel({
                    type: "details",
                    tab: "fault",
                  })}
                />
              ),
              lastCommentCard: (
                <LastCommentCard
                  projectId={project.id}
                  timezone={project.timezone}
                  event={event}
                  onClick={onClickSidePanel({ type: "comments" })}
                />
              ),
              actionIdentifiedCard:
                event.identifiedAction !== "" && event.identifiedAction !== null ? (
                  <ActionIdentifiedCard
                    text={event.identifiedAction}
                    onClick={
                      canUserEditProject
                        ? onClickSidePanel({
                            type: "edit",
                            focusField: "actionIdentified",
                          })
                        : undefined
                    }
                  />
                ) : undefined,
            }}
            navigation={<EventNavigation />}
          />
          <Outlet context={{ event, project } satisfies ManualFaultEventOutletContext} />
        </div>

        {isFolded ? (
          <div />
        ) : selectedSidePanel.type === "activity" ? (
          <SuspenseSidePanel>
            <Activity
              isFolded={isFolded}
              project={project}
              manualFaultEvent={event}
              mode="activity"
              closePanel={() => setFolded(true)}
            />
          </SuspenseSidePanel>
        ) : selectedSidePanel.type === "comments" ? (
          <SuspenseSidePanel>
            <Activity
              isFolded={isFolded}
              project={project}
              manualFaultEvent={event}
              mode="comments"
              closePanel={() => setFolded(true)}
            />
          </SuspenseSidePanel>
        ) : selectedSidePanel.type === "edit" ? (
          <SuspenseSidePanel>
            <SidePanelEditForm
              isFolded={isFolded}
              project={project}
              manualFaultEvent={{
                id: event.id,
                name: event.name,
                action: event.identifiedAction,
                startsAt: event.startsAt,
                closedAt: event.metadata.endsAt,
                status: event.metadata.status,
                fault: {
                  id: event.metadata.manualFault.id,
                  systemId: event.metadata.manualFault.systemId,
                },
                description: event.description,
                windows: event.windows,
                priority: event.priority,
                reparability: event.reparability,
                identifiedAction: event.identifiedAction,
              }}
              focusField={selectedSidePanel.focusField}
              closePanel={() => setFolded(true)}
            />
          </SuspenseSidePanel>
        ) : (
          <SuspenseSidePanel>
            <Details
              project={project}
              manualEvent={event}
              isFolded={isFolded}
              closePanel={() => setFolded(true)}
              tab={selectedSidePanel.tab}
              setTab={setSelectedSidePanel}
            />
          </SuspenseSidePanel>
        )}
      </div>

      <Modal
        title={t("project.manual_fault_event.modal.delete.title")}
        isOpen={modal === DELETE_MANUAL_FAULT_EVENT_MODAL}
        onRequestClose={closeModal}
      >
        <ModalFormRemoveManualFaultEvent
          projectId={project.id}
          manualFaultEvent={event}
          onRequestClose={closeModal}
        />
      </Modal>
    </>
  );
};

export default ManualFaultEvent;

//#region CSS
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global() {
    :root {
      --side-bar-width: 400px;
      --section-column-transition-duration: 350ms;
      --section-column-transition-ease: cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
`;
const manualFaultEvent = css`
  --right-column-width: var(--side-bar-width);
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--right-column-width);
  grid-auto-rows: auto;
  flex-grow: 1;

  transition: grid-template-columns var(--section-column-transition-duration)
    var(--section-column-transition-ease);
  color: ${colors.carbon900};

  &.isFolded {
    --right-column-width: 0;
  }
`;
const manualFaultEventContent = css`
  &.${EXTEND_CONTENT} {
    display: flex;
    flex-direction: column;
  }
`;
const tag = css`
  flex-shrink: 0;
`;
//#endregion CSS
